import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { trpcClient } from "@/lib/trpc";
import { useConfigItems } from "@/hooks/useConfigItems";

/**
 * Phân loại dùng chung cho mẫu kiểm tra QC và phiếu IQC / IPQC / FQC — một nguồn duy nhất:
 *  1. danh mục Cấu hình > Chất lượng (QC_MATERIAL_GROUP / QC_PROCESS_STAGE / QC_PRODUCT_GROUP) — người dùng tự thêm/sửa;
 *  2. khoá cấu hình cũ (QC_MATERIAL_CATEGORIES / QC_OFFSET_STAGES / QC_PRODUCT_CATEGORIES) nếu còn;
 *  3. giá trị mặc định dưới đây (trước đây viết cứng, mỗi màn hình một kiểu).
 */
export type QcOption = { code: string; label: string };

export const QC_CLASSIFICATION_CATEGORIES = {
  materialGroups: "QC_MATERIAL_GROUP",
  processStages: "QC_PROCESS_STAGE",
  productGroups: "QC_PRODUCT_GROUP",
} as const;

const DEFAULTS: Record<keyof typeof QC_CLASSIFICATION_CATEGORIES, QcOption[]> = {
  materialGroups: [
    { code: "PAPER", label: "Giấy" },
    { code: "FILM", label: "Màng cán" },
    { code: "INK_CHEMICAL", label: "Mực & hoá chất" },
    { code: "ADHESIVE", label: "Keo" },
    { code: "TOOLING", label: "Khuôn & bản kẽm" },
    { code: "CORRUGATED", label: "Phôi sóng" },
  ],
  processStages: [
    { code: "CUTTING", label: "1. Cắt giấy" },
    { code: "PRINTING", label: "2. In Offset" },
    { code: "LAMINATING", label: "3. Cán màng" },
    { code: "COATING", label: "4. Tráng phủ UV" },
    { code: "DIE_CUTTING", label: "5. Bế / Ép kim" },
    { code: "GLUING", label: "6. Dán hộp" },
  ],
  productGroups: [
    { code: "HOP_MEM", label: "Hộp giấy mềm" },
    { code: "HOP_CUNG", label: "Hộp cứng" },
    { code: "THUNG_CARTON", label: "Thùng carton sóng" },
    { code: "HOP_SONG", label: "Hộp bồi sóng" },
    { code: "TUI_GIAY", label: "Túi giấy" },
    { code: "NHAN_DECAL", label: "Nhãn & decal" },
  ],
};

const LEGACY_KEYS: Record<keyof typeof QC_CLASSIFICATION_CATEGORIES, string> = {
  materialGroups: "QC_MATERIAL_CATEGORIES",
  processStages: "QC_OFFSET_STAGES",
  productGroups: "QC_PRODUCT_CATEGORIES",
};

const fromConfigItems = (items: any[] | undefined): QcOption[] =>
  (items || [])
    .filter((i: any) => i && (i.code || i.name) && i.isActive !== false)
    .map((i: any) => ({ code: String(i.code || i.name).toUpperCase(), label: String(i.name || i.code) }));

const fromLegacy = (v: unknown): QcOption[] =>
  Array.isArray(v) ? v.filter((x: any) => x?.code).map((x: any) => ({ code: String(x.code).toUpperCase(), label: String(x.label || x.name || x.code) })) : [];

export function useQcClassification() {
  const material = useConfigItems(QC_CLASSIFICATION_CATEGORIES.materialGroups);
  const stage = useConfigItems(QC_CLASSIFICATION_CATEGORIES.processStages);
  const product = useConfigItems(QC_CLASSIFICATION_CATEGORIES.productGroups);
  const { data: sysConfigs } = useQuery({
    queryKey: ["configs", "qc-classification-legacy"],
    queryFn: () => trpcClient.config.getAll.query(),
    staleTime: 5 * 60 * 1000,
  });

  return useMemo(() => {
    const pick = (key: keyof typeof QC_CLASSIFICATION_CATEGORIES, items: any[] | undefined): QcOption[] => {
      const dynamic = fromConfigItems(items);
      if (dynamic.length) return dynamic;
      const legacy = fromLegacy((sysConfigs as any)?.[LEGACY_KEYS[key]]);
      return legacy.length ? legacy : DEFAULTS[key];
    };
    const materialGroups = pick("materialGroups", material.data as any[]);
    const processStages = pick("processStages", stage.data as any[]);
    const productGroups = pick("productGroups", product.data as any[]);
    const labelOf = (list: QcOption[], code?: string | null) =>
      code ? list.find((o) => o.code === String(code).toUpperCase())?.label || code : "";
    return {
      materialGroups,
      processStages,
      productGroups,
      materialLabel: (code?: string | null) => labelOf(materialGroups, code),
      stageLabel: (code?: string | null) => labelOf(processStages, code),
      productGroupLabel: (code?: string | null) => labelOf(productGroups, code),
    };
  }, [material.data, stage.data, product.data, sysConfigs]);
}
